/* =========================================================
   ApexMane — Animation Engine
   ========================================================= */
(function() {
  'use strict';

  const APEX = window.APEX = window.APEX || {};

  // ---------- 1. Page loader (DISABLED) ----------
  // Splash loader has been removed site-wide. We still:
  //   - mark the html element as JS-ready (other CSS selectors depend on it)
  //   - defensively strip any cached apex-loading class from <body> so
  //     scroll is never locked even if a stale CDN response delivers the
  //     old HTML.
  document.documentElement.classList.add('apex-js-ready');
  if (document.body) {
    document.body.classList.remove('apex-loading');
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      if (document.body) document.body.classList.remove('apex-loading');
    });
  }
  // Also drop any stale loader element if one slipped through (CDN cache)
  document.addEventListener('DOMContentLoaded', function () {
    var _staleLoader = document.querySelector('.apex-loader');
    if (_staleLoader && _staleLoader.parentNode) {
      _staleLoader.parentNode.removeChild(_staleLoader);
    }
  });

  // ---------- 2. IntersectionObserver scroll reveals ----------
  function setupReveals() {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('.apex-reveal, .apex-reveal-child, .apex-reveal--scale, .apex-reveal--left, .apex-reveal--right, .apex-img-reveal').forEach(el => el.classList.add('is-visible'));
      document.querySelectorAll('.shopify-section[id*="apex"]').forEach(el => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible', 'is-in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    document.querySelectorAll(
      '.apex-reveal, .apex-reveal-child, .apex-reveal--scale, .apex-reveal--left, .apex-reveal--right, .apex-img-reveal, .shopify-section[id*="apex"]'
    ).forEach(el => io.observe(el));
  }

  // ---------- 3. Count-up animation for stats ----------
  function countUp(el) {
    const raw = el.textContent.trim();
    const match = raw.match(/^([\d,]+(?:\.\d+)?)(.*)$/);
    if (!match) return;
    const target = parseFloat(match[1].replace(/,/g, ''));
    const suffix = match[2] || '';
    if (isNaN(target)) return;
    const duration = 1600;
    const start = performance.now();
    const isInt = Number.isInteger(target);

    function tick(now) {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 4);
      const value = target * eased;
      el.textContent = (isInt ? Math.round(value).toLocaleString() : value.toFixed(1)) + suffix;
      if (t < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }
  function setupCountUps() {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          countUp(e.target);
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.5 });
    document.querySelectorAll('.apex-stat__num, [data-apex-countup]').forEach(el => io.observe(el));
  }

  // ---------- 4. Top scroll progress bar ----------
  function setupScrollBar() {
    let bar = document.querySelector('.apex-scroll-bar');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'apex-scroll-bar';
      document.body.appendChild(bar);
    }
    let ticking = false;
    function update() {
      const scrolled = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const pct = max > 0 ? (scrolled / max) * 100 : 0;
      bar.style.width = pct + '%';
      ticking = false;
    }
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
    update();
  }

  // ---------- 5. Header scroll-shrink ----------
  function setupHeaderScroll() {
    const header = document.querySelector('.shopify-section-header');
    if (!header) return;
    let ticking = false;
    function update() {
      header.classList.toggle('is-scrolled', window.scrollY > 24);
      ticking = false;
    }
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  // ---------- 6. Cart helpers ----------
  APEX.openCartDrawer = function() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return false;
    if (typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }
    drawer.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    return true;
  };

  // Format money with currency symbol (shared helper)
  function apexFormatMoney(cents) {
    const amt = (cents/100).toFixed(2);
    const code = window.Shopify && Shopify.currency && Shopify.currency.active ? Shopify.currency.active : 'USD';
    const symbols = { ILS: '₪', USD: '$', EUR: '€', GBP: '£', CAD: '$', AUD: '$', JPY: '¥' };
    return (symbols[code] || code) + amt;
  }

  // Render cart drawer upsells — carousel layout with toggle + savings summary
  APEX.renderCartUpsells = function () {
    const container = document.querySelector('[data-apex-cart-upsells-container]');
    if (!container) return;
    const track = container.querySelector('[data-apex-cart-ups-track]');
    const dotsWrap = container.querySelector('[data-apex-cart-ups-dots]');
    const prevBtn = container.querySelector('[data-apex-cart-ups-prev]');
    const nextBtn = container.querySelector('[data-apex-cart-ups-next]');
    const savingsEl = container.querySelector('[data-apex-cart-ups-savings]');
    const totalEl = container.querySelector('[data-apex-cart-ups-total]');
    if (!track) return;

    const dataEl = document.querySelector('[data-apex-cart-upsells-data]');
    if (!dataEl) { container.hidden = true; return; }

    let upsells = [];
    try { upsells = JSON.parse(dataEl.textContent); } catch (e) { upsells = []; }
    upsells = upsells.filter(u => u && u.variant_id);
    if (!upsells.length) { container.hidden = true; return; }

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(r => r.json())
      .then(cart => {
        const cartVariants = new Set((cart.items || []).map(i => String(i.variant_id)));
        container.hidden = false;

        // Build slides (dir="rtl" on each slide for Hebrew content, but track is LTR for transform math)
        track.innerHTML = upsells.map(u => {
          const inCart = cartVariants.has(String(u.variant_id));
          const hasCompare = u.compare_at_price && u.compare_at_price > u.price;
          return `
            <div class="apex-ups-slide" dir="rtl" data-variant-id="${u.variant_id}" data-price="${u.price}" data-compare="${u.compare_at_price || 0}">
              <label class="apex-ups-slide__toggle" aria-label="הוסף/הסר מהעגלה">
                <input type="checkbox" data-apex-cart-ups-toggle ${inCart ? 'checked' : ''}>
                <span class="apex-ups-slide__switch"></span>
              </label>
              <div class="apex-ups-slide__info">
                <div class="apex-ups-slide__prices">
                  <span class="apex-ups-slide__price-current">${apexFormatMoney(u.price)}</span>
                  ${hasCompare ? `<span class="apex-ups-slide__price-compare">${apexFormatMoney(u.compare_at_price)}</span>` : ''}
                </div>
              </div>
              <h4 class="apex-ups-slide__title">${escapeForHtml(u.label || '')}</h4>
              <div class="apex-ups-slide__image">
                ${u.image ? `<img src="${u.image}" alt="${escapeForHtml(u.label || '')}" loading="lazy">` : '🎁'}
              </div>
            </div>
          `;
        }).join('');

        // Dots
        if (dotsWrap) {
          dotsWrap.innerHTML = upsells.map((_, i) => `
            <button type="button" class="apex-ups__dot${i === 0 ? ' is-active' : ''}" data-apex-cart-ups-dot="${i}" aria-label="עבור לסלייד ${i+1}"></button>
          `).join('');
        }

        // Carousel navigation using CSS transform (reliable across RTL/LTR browsers)
        let currentIdx = 0;
        function goTo(i) {
          currentIdx = Math.max(0, Math.min(upsells.length - 1, i));
          track.style.transform = `translateX(-${currentIdx * 100}%)`;
          if (dotsWrap) {
            dotsWrap.querySelectorAll('.apex-ups__dot').forEach((d, idx) => {
              d.classList.toggle('is-active', idx === currentIdx);
            });
          }
          if (prevBtn) prevBtn.disabled = currentIdx === 0;
          if (nextBtn) nextBtn.disabled = currentIdx >= upsells.length - 1;
        }

        if (prevBtn) { prevBtn.onclick = () => goTo(currentIdx - 1); }
        if (nextBtn) { nextBtn.onclick = () => goTo(currentIdx + 1); }
        if (dotsWrap) {
          dotsWrap.querySelectorAll('[data-apex-cart-ups-dot]').forEach(d => {
            d.onclick = () => goTo(parseInt(d.getAttribute('data-apex-cart-ups-dot'), 10) || 0);
          });
        }
        // Initialize nav states
        goTo(0);

        // Wire toggles
        track.querySelectorAll('[data-apex-cart-ups-toggle]').forEach(input => {
          input.addEventListener('change', async function () {
            const slide = input.closest('.apex-ups-slide');
            if (!slide) return;
            const variantId = slide.getAttribute('data-variant-id');
            if (!variantId) return;
            input.disabled = true;

            try {
              if (input.checked) {
                // Add
                const fd = new FormData();
                fd.append('id', variantId);
                fd.append('quantity', '1');
                fd.append('sections', 'cart-drawer,cart-icon-bubble');
                fd.append('sections_url', '/cart');
                const res = await fetch('/cart/add.js', {
                  method: 'POST',
                  headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
                  credentials: 'same-origin',
                  body: fd
                });
                const json = await res.json();
                if (json.status && json.status >= 400) throw new Error(json.message || 'Error');
                applyCartSections(json.sections || {});
              } else {
                // Remove (set qty to 0 for that variant)
                const res = await fetch('/cart/change.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
                  credentials: 'same-origin',
                  body: JSON.stringify({ id: variantId, quantity: 0, sections: 'cart-drawer,cart-icon-bubble', sections_url: '/cart' })
                });
                const json = await res.json();
                applyCartSections(json.sections || {});
              }
              // After changing, re-render upsells for updated state
              requestAnimationFrame(() => APEX.renderCartUpsells());
            } catch (err) {
              console.warn('[cart upsell toggle] failed:', err);
              input.checked = !input.checked;
            } finally {
              input.disabled = false;
            }
          });
        });

        // Update savings + total summary based on cart.js data
        updateUpsellSummary(cart, upsells, cartVariants, savingsEl, totalEl);
      })
      .catch(() => { container.hidden = true; });
  };

  function applyCartSections(sections) {
    if (sections['cart-drawer']) {
      const drawerEl = document.getElementById('CartDrawer');
      if (drawerEl) {
        const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const newInner = parsed.querySelector('#CartDrawer');
        if (newInner) drawerEl.innerHTML = newInner.innerHTML;
      }
      const drawer = document.querySelector('cart-drawer');
      if (drawer && drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');
    }
    if (sections['cart-icon-bubble']) {
      const bubbleEl = document.getElementById('cart-icon-bubble');
      if (bubbleEl) {
        const parsed = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newBubble = parsed.querySelector('#cart-icon-bubble');
        if (newBubble) bubbleEl.innerHTML = newBubble.innerHTML;
      }
    }
  }

  function updateUpsellSummary(cart, upsells, cartVariants, savingsEl, totalEl) {
    // TOTAL SAVINGS — aggregates:
    //   A. compare_at_price savings on ALL items in cart (sale price vs regular price)
    //   B. cart-level discount codes + automatic discounts (from total_discount)
    let savings = 0;

    // A. Sum compare_at savings across ALL cart items
    (cart.items || []).forEach(item => {
      const original = item.original_price || 0;  // Shopify's "original_price" = pre-discount price per unit
      const final = item.final_price || item.price || 0;
      const qty = item.quantity || 1;
      if (original > final) {
        savings += (original - final) * qty;
      }
    });

    // B. Add cart-level discount (applied codes / automatic discounts)
    savings += (cart.total_discount || 0);

    // Fallback: if somehow original_total_price > total_price, use that
    if (savings === 0 && cart.original_total_price && cart.original_total_price > cart.total_price) {
      savings = cart.original_total_price - cart.total_price;
    }

    if (savingsEl) savingsEl.textContent = savings > 0 ? '-' + apexFormatMoney(savings) : apexFormatMoney(0);
    if (totalEl) totalEl.textContent = apexFormatMoney(cart.total_price || 0);
  }

  function escapeForHtml(s) {
    return String(s || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  // Render cart upsells on page load + whenever cart drawer opens
  document.addEventListener('DOMContentLoaded', () => APEX.renderCartUpsells());

  // Hook into any trigger that opens the cart drawer
  document.addEventListener('click', function (e) {
    const target = e.target.closest('[data-cart-icon], #cart-icon-bubble, .header__icon--cart, a[href="/cart"], a[href$="#cart"]');
    if (target) setTimeout(() => APEX.renderCartUpsells(), 300);
  });

  // Also re-render whenever Dawn publishes a cart update
  if (typeof window !== 'undefined') {
    const tryHookCartUpdate = () => {
      if (typeof window.subscribe === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
          setTimeout(() => APEX.renderCartUpsells(), 100);
        });
        return true;
      }
      return false;
    };
    if (!tryHookCartUpdate()) {
      // Retry after Dawn scripts load
      setTimeout(tryHookCartUpdate, 500);
      setTimeout(tryHookCartUpdate, 1500);
    }
  }

  // MutationObserver fallback — watch for cart drawer becoming active
  const drawerEl = document.querySelector('cart-drawer, .drawer');
  if (drawerEl && 'MutationObserver' in window) {
    const mo = new MutationObserver(muts => {
      muts.forEach(m => {
        if (m.type === 'attributes' && m.attributeName === 'class') {
          if (drawerEl.classList.contains('active') || drawerEl.classList.contains('is-open')) {
            setTimeout(() => APEX.renderCartUpsells(), 100);
          }
        }
      });
    });
    mo.observe(drawerEl, { attributes: true, attributeFilter: ['class'] });
  }

  APEX.refreshCartSections = async function() {
    try {
      const drawer = document.querySelector('cart-drawer');
      const bubbleEl = document.getElementById('cart-icon-bubble');
      const sectionIds = ['cart-drawer', 'cart-icon-bubble'];
      const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await cartRes.json();
      const secRes = await fetch('/?sections=' + sectionIds.join(','), { credentials: 'same-origin' });
      const sections = await secRes.json();
      // Render cart drawer
      if (drawer && sections['cart-drawer']) {
        const drawerEl = document.getElementById('CartDrawer');
        if (drawerEl) {
          const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
          const newInner = parsed.querySelector('#CartDrawer');
          if (newInner) drawerEl.innerHTML = newInner.innerHTML;
        }
        if (drawer.classList.contains('is-empty') && cart.item_count > 0) {
          drawer.classList.remove('is-empty');
        }
      }
      // Render cart icon bubble
      if (bubbleEl && sections['cart-icon-bubble']) {
        const parsed = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newBubble = parsed.querySelector('#cart-icon-bubble');
        if (newBubble) bubbleEl.innerHTML = newBubble.innerHTML;
      }
      return cart;
    } catch (e) {
      console.warn('[ApexMane] refresh failed', e);
      return null;
    }
  };

  // ---------- 7. Rich Add-to-Cart Popup ----------
  function ensurePopupNode() {
    let pop = document.querySelector('.apex-atc-popup');
    if (pop) return pop;
    pop = document.createElement('div');
    pop.className = 'apex-atc-popup';
    pop.setAttribute('role', 'dialog');
    pop.setAttribute('aria-label', 'פריט נוסף לעגלה');
    pop.innerHTML = `
      <div class="apex-atc-popup__head">
        <div class="apex-atc-popup__head-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="square"><path d="M5 12l4 4 10-10"/></svg>
          <span>נוסף לעגלה</span>
        </div>
        <button class="apex-atc-popup__close" aria-label="סגור">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="2" stroke-linecap="square"/></svg>
        </button>
      </div>
      <div class="apex-atc-popup__body">
        <div class="apex-atc-popup__img"></div>
        <div class="apex-atc-popup__info">
          <h3 class="apex-atc-popup__name"></h3>
          <p class="apex-atc-popup__variant"></p>
          <div class="apex-atc-popup__price"></div>
        </div>
      </div>
      <div class="apex-atc-popup__urgency">
        <span class="apex-atc-popup__urgency-item">
          <span class="apex-atc-popup__pulse"></span>
          <strong data-apex-viewers>30</strong>&nbsp;צופים עכשיו
        </span>
        <span class="apex-atc-popup__urgency-sep"></span>
        <span class="apex-atc-popup__urgency-item">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>
          שמור <strong data-apex-cart-timer>15:00</strong>
        </span>
      </div>
      <div class="apex-atc-popup__trust">
        <span><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z"/></svg> 120 יום החזר כספי</span>
      </div>
      <div class="apex-atc-popup__upsells" data-apex-upsells-container hidden>
        <div class="apex-atc-popup__upsells-title">הוסיפו לעגלה (מומלץ)</div>
        <div class="apex-atc-popup__upsells-list" data-apex-upsells-list></div>
      </div>
      <div class="apex-atc-popup__cart-meta">
        <span><strong class="apex-atc-popup__count">0</strong> פריטים בעגלה</span>
        <span class="apex-atc-popup__total">$0.00</span>
      </div>
      <div class="apex-atc-popup__actions">
        <button type="button" class="apex-atc-popup__btn apex-atc-popup__btn--secondary" data-apex-view-cart>צפה בעגלה</button>
        <a class="apex-atc-popup__btn apex-atc-popup__btn--primary" href="/checkout">לתשלום
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 7h12m0 0L8 2m5 5L8 12" stroke="currentColor" stroke-width="2" stroke-linecap="square"/></svg>
        </a>
      </div>
    `;
    document.body.appendChild(pop);

    pop.querySelector('.apex-atc-popup__close').addEventListener('click', () => APEX.closePopup());
    pop.querySelector('[data-apex-view-cart]').addEventListener('click', () => {
      APEX.closePopup();
      APEX.openCartDrawer();
    });
    return pop;
  }

  // Render upsell products in the popup
  // Reads from product-hero upsells FIRST, falls back to cart-drawer upsells (unified config)
  function renderUpsells(pop) {
    const container = pop.querySelector('[data-apex-upsells-container]');
    const list = pop.querySelector('[data-apex-upsells-list]');
    if (!container || !list) return;

    // Try product-hero data first, then cart-drawer data
    let dataEl = document.querySelector('[data-apex-upsells-data]');
    if (!dataEl) dataEl = document.querySelector('[data-apex-cart-upsells-data]');
    if (!dataEl) { container.hidden = true; return; }

    let upsells = [];
    try { upsells = JSON.parse(dataEl.textContent); } catch (e) { upsells = []; }
    upsells = upsells.filter(u => u && u.variant_id);
    if (!upsells.length) { container.hidden = true; return; }

    container.hidden = false;
    list.innerHTML = upsells.map(u => `
      <div class="apex-atc-popup__upsell" data-variant-id="${u.variant_id}">
        <span class="apex-atc-popup__upsell-icon${u.image ? ' has-image' : ''}">
          ${u.image ? `<img src="${u.image}" alt="${escapeForHtml(u.label || '')}" loading="lazy">` : (u.icon || '🎁')}
        </span>
        <div class="apex-atc-popup__upsell-info">
          <div class="apex-atc-popup__upsell-label">${escapeForHtml(u.label || '')}</div>
          <div class="apex-atc-popup__upsell-price">${formatMoney(u.price || 0)}</div>
        </div>
        <button type="button" class="apex-atc-popup__upsell-btn" data-apex-upsell-add="${u.variant_id}" aria-label="הוסף לעגלה">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="square"><path d="M7 2v10M2 7h10"/></svg>
          <span>הוסף</span>
        </button>
      </div>
    `).join('');

    // Wire add buttons
    list.querySelectorAll('[data-apex-upsell-add]').forEach(btn => {
      if (btn.dataset.apexBound) return;
      btn.dataset.apexBound = 'true';
      btn.addEventListener('click', async function () {
        const variantId = btn.getAttribute('data-apex-upsell-add');
        if (!variantId) return;
        btn.disabled = true;
        btn.classList.add('is-loading');
        try {
          const fd = new FormData();
          fd.append('id', variantId);
          fd.append('quantity', '1');
          fd.append('sections', 'cart-drawer,cart-icon-bubble');
          fd.append('sections_url', '/cart');
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
            credentials: 'same-origin',
            body: fd
          });
          const json = await res.json();
          if (json.status && json.status >= 400) throw new Error(json.message || 'Error');

          // Update cart icon bubble (small, non-disruptive — doesn't close popup)
          const sections = json.sections || {};
          if (sections['cart-icon-bubble']) {
            const bubbleEl = document.getElementById('cart-icon-bubble');
            if (bubbleEl) {
              const parsed = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
              const newBubble = parsed.querySelector('#cart-icon-bubble');
              if (newBubble) bubbleEl.innerHTML = newBubble.innerHTML;
            }
          }
          // Update cart-drawer sections in background (for when user opens drawer later)
          if (sections['cart-drawer']) {
            const drawerEl = document.getElementById('CartDrawer');
            if (drawerEl) {
              const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
              const newInner = parsed.querySelector('#CartDrawer');
              if (newInner) drawerEl.innerHTML = newInner.innerHTML;
            }
            const drawer = document.querySelector('cart-drawer');
            if (drawer && drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');
          }

          // Keep popup OPEN — just update the button state to show success
          btn.classList.remove('is-loading');
          btn.classList.add('is-added');
          btn.disabled = true;
          btn.innerHTML = `
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="square"><path d="M2 7l4 4 6-7"/></svg>
            <span>נוסף ✓</span>
          `;

          // Extend popup auto-close timer so user has time to see the update
          if (popupTimer) { clearTimeout(popupTimer); popupTimer = setTimeout(() => APEX.closePopup(), 12000); }

          // Refresh cart totals in popup
          fetch('/cart.js', { credentials: 'same-origin' })
            .then(r => r.json())
            .then(cart => {
              const popup = document.querySelector('.apex-atc-popup');
              if (popup && cart) {
                const countEl = popup.querySelector('.apex-atc-popup__count');
                const totalEl = popup.querySelector('.apex-atc-popup__total');
                if (countEl) countEl.textContent = cart.item_count;
                if (totalEl) totalEl.textContent = formatMoney(cart.total_price);
              }
            })
            .catch(() => {});
        } catch (err) {
          console.warn('[Revivex upsell] failed:', err);
          btn.disabled = false;
          btn.classList.remove('is-loading');
        }
      });
    });
  }

  let popupTimer = null;
  APEX.closePopup = function() {
    const pop = document.querySelector('.apex-atc-popup');
    if (pop) pop.classList.remove('is-visible');
    if (popupTimer) { clearTimeout(popupTimer); popupTimer = null; }
  };

  APEX.showPopup = function(payload) {
    const pop = ensurePopupNode();
    const imgWrap = pop.querySelector('.apex-atc-popup__img');
    const name = pop.querySelector('.apex-atc-popup__name');
    const variant = pop.querySelector('.apex-atc-popup__variant');
    const price = pop.querySelector('.apex-atc-popup__price');
    const countEl = pop.querySelector('.apex-atc-popup__count');
    const totalEl = pop.querySelector('.apex-atc-popup__total');

    imgWrap.innerHTML = payload.image
      ? `<img src="${payload.image}" alt="">`
      : '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="color:#7DD3D8"><path d="M3 5h2l2 9h11l2-7H6"/></svg>';
    name.textContent = payload.title || 'Item';
    variant.textContent = payload.variantTitle && payload.variantTitle !== 'Default Title' ? payload.variantTitle : '';
    price.textContent = payload.price || '';
    countEl.textContent = payload.itemCount != null ? payload.itemCount : '';
    totalEl.textContent = payload.total || '';

    // Render upsells (pulls from <script data-apex-upsells-data> in the DOM)
    try { renderUpsells(pop); } catch (e) { /* upsells are optional */ }

    requestAnimationFrame(() => pop.classList.add('is-visible'));
    if (popupTimer) clearTimeout(popupTimer);
    popupTimer = setTimeout(() => APEX.closePopup(), 12000); // extended to 12s so user has time to add upsells
  };

  // Map currency codes to symbols for nicer display
  const APEX_CURRENCY_SYMBOLS = {
    ILS: '₪',
    USD: '$',
    EUR: '€',
    GBP: '£',
    CAD: '$',
    AUD: '$',
    JPY: '¥',
    CNY: '¥',
    RUB: '₽',
    INR: '₹',
    BRL: 'R$',
    MXN: '$',
    CHF: 'CHF',
    SEK: 'kr',
    NOK: 'kr',
    DKK: 'kr',
    PLN: 'zł',
    TRY: '₺',
    ZAR: 'R',
    KRW: '₩'
  };

  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const code = window.Shopify && Shopify.currency && Shopify.currency.active
      ? Shopify.currency.active
      : 'USD';
    const symbol = APEX_CURRENCY_SYMBOLS[code] || code;
    // For Hebrew (RTL), shekel symbol comes before the number: ₪899.92
    // For most others, symbol before: $899.92
    return symbol + amount;
  }

  // ---------- 8. Override ATC: AJAX submit + popup, no auto-drawer ----------
  function attachAtcHandler() {
    document.querySelectorAll('product-form form, form[action*="/cart/add"]').forEach(form => {
      if (form.dataset.apexBound) return;
      form.dataset.apexBound = 'true';

      form.addEventListener('submit', async function(e) {
        // Only intercept if we're on a real product form
        const submitBtn = form.querySelector('[type="submit"]');
        if (!submitBtn || submitBtn.disabled) return;

        e.preventDefault();
        e.stopPropagation();

        // Spinner / button state
        submitBtn.classList.add('loading');
        const spinner = form.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
        const inner = submitBtn.querySelector('.apex-ph-atc__inner');
        if (inner) inner.style.opacity = '0.4';
        submitBtn.setAttribute('aria-busy', 'true');

        try {
          const formData = new FormData(form);
          // ⚡ PERF: Request cart sections in the same POST — eliminates 2 network roundtrips.
          // Instead of: POST /cart/add.js → GET /cart.js → GET /?sections=... (3 sequential)
          // We now do:  POST /cart/add.js (with sections= body param) (1 request)
          formData.append('sections', 'cart-drawer,cart-icon-bubble');
          formData.append('sections_url', '/cart');

          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
            credentials: 'same-origin',
            body: formData
          });
          const json = await res.json();
          if (json.status && json.status >= 400) {
            throw new Error(json.message || json.description || 'Add to cart failed');
          }

          // ⚡ PERF: Sections HTML came in the same response — inject directly, no extra fetches
          const sections = json.sections || {};

          // Render cart drawer
          if (sections['cart-drawer']) {
            const drawerEl = document.getElementById('CartDrawer');
            if (drawerEl) {
              const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
              const newInner = parsed.querySelector('#CartDrawer');
              if (newInner) drawerEl.innerHTML = newInner.innerHTML;
            }
            const drawer = document.querySelector('cart-drawer');
            if (drawer && drawer.classList.contains('is-empty')) {
              drawer.classList.remove('is-empty');
            }
          }

          // Render cart icon bubble
          if (sections['cart-icon-bubble']) {
            const bubbleEl = document.getElementById('cart-icon-bubble');
            if (bubbleEl) {
              const parsed = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
              const newBubble = parsed.querySelector('#cart-icon-bubble');
              if (newBubble) bubbleEl.innerHTML = newBubble.innerHTML;
            }
          }

          // Show popup IMMEDIATELY (don't wait for cart count)
          APEX.showPopup({
            image: json.featured_image && json.featured_image.url
              ? json.featured_image.url
              : (json.image || ''),
            title: json.product_title || json.title || 'Item',
            variantTitle: json.variant_title || '',
            price: formatMoney(json.final_line_price || json.price || 0),
            itemCount: '',
            total: ''
          });

          // Fetch cart totals in BACKGROUND (non-blocking) — updates popup when ready
          fetch('/cart.js', { credentials: 'same-origin' })
            .then(r => r.json())
            .then(cart => {
              const popup = document.querySelector('.apex-atc-popup');
              if (popup && cart) {
                const countEl = popup.querySelector('.apex-atc-popup__count');
                const totalEl = popup.querySelector('.apex-atc-popup__total');
                if (countEl) countEl.textContent = cart.item_count;
                if (totalEl) totalEl.textContent = formatMoney(cart.total_price);
              }
            })
            .catch(() => {});

          // Pulse ATC button
          submitBtn.classList.add('is-added');
          setTimeout(() => submitBtn.classList.remove('is-added'), 1800);

          // Publish Dawn cartUpdate so other listeners (gift logic) fire
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'apex-atc',
              productVariantId: formData.get('id'),
              cartData: json
            });
          }
        } catch (err) {
          console.error('[ApexMane] ATC error:', err);
          const errBox = form.querySelector('.product-form__error-message-wrapper');
          if (errBox) {
            errBox.removeAttribute('hidden');
            const msg = errBox.querySelector('.product-form__error-message');
            if (msg) msg.textContent = err.message || 'Sorry, this item could not be added.';
          }
        } finally {
          submitBtn.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
          if (inner) inner.style.opacity = '';
          submitBtn.removeAttribute('aria-busy');
        }
      }, true);
    });
  }

  // ---------- 8. Smooth in-page anchor scroll with offset ----------
  function setupAnchors() {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute('href').slice(1);
      if (!id) return;
      const target = document.getElementById(id) || document.querySelector(`[id="${id}"]`);
      if (!target) return;
      e.preventDefault();
      const header = document.querySelector('.shopify-section-header');
      const offset = header ? header.offsetHeight + 12 : 0;
      window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' });
    });
  }

  // ---------- 9. Magnetic cursor on big buttons ----------
  function setupMagnetic() {
    const buttons = document.querySelectorAll('.apex-ph-atc, .apex-cta-' + (document.querySelector('[class*="apex-cta-"]') ? '' : '__btn'));
    document.querySelectorAll('.apex-ph-atc, [class*="apex-cta-"][class*="__btn"], [class*="apex-grt-"][class*="__btn"], [class*="apex-out-"][class*="__btn"]').forEach(btn => {
      btn.addEventListener('mousemove', (e) => {
        const r = btn.getBoundingClientRect();
        const mx = e.clientX - r.left - r.width / 2;
        const my = e.clientY - r.top - r.height / 2;
        btn.style.transform = `translate(${mx * 0.08}px, ${my * 0.18}px) translateY(-2px)`;
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.transform = '';
      });
    });
  }

  // ---------- 10. Mobile sticky ATC bar (product page only) ----------
  function setupMobileStickyAtc() {
    if (window.innerWidth > 750) return;
    const productHero = document.querySelector('[class*="apex-ph-"]');
    if (!productHero) return;
    const mainAtc = productHero.querySelector('.apex-ph-atc');
    if (!mainAtc) return;

    const bar = document.createElement('div');
    bar.className = 'apex-mobile-sticky-atc';
    bar.innerHTML = `
      <div class="apex-mobile-sticky-atc__price">
        <small>Total</small>
        <span data-apex-mobile-price>${productHero.querySelector('[data-apex-price]')?.textContent || ''}</span>
      </div>
      <button type="button" class="apex-mobile-sticky-atc__btn" data-apex-mobile-atc>
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M3 5h2l2 9h9l2-7H6" stroke="currentColor" stroke-width="2" stroke-linecap="square"/></svg>
        ADD TO CART
      </button>
    `;
    document.body.appendChild(bar);

    // Sync price when variant changes
    const priceEl = productHero.querySelector('[data-apex-price]');
    if (priceEl) {
      const obs = new MutationObserver(() => {
        const out = bar.querySelector('[data-apex-mobile-price]');
        if (out) out.textContent = priceEl.textContent;
      });
      obs.observe(priceEl, { childList: true, characterData: true, subtree: true });
    }

    // Click triggers main ATC
    bar.querySelector('[data-apex-mobile-atc]').addEventListener('click', () => {
      mainAtc.click();
    });

    // Show/hide based on scroll past hero
    const heroBottom = () => {
      const r = productHero.getBoundingClientRect();
      return r.bottom < 200; // hero scrolled mostly out
    };
    function update() {
      bar.classList.toggle('is-visible', heroBottom());
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  // ---------- 10c. Cart Urgency: live viewers + reservation timer ----------
  function setupCartUrgency() {
    const RESERVE_KEY = 'apex_cart_reserve_until';
    const RESERVE_MINUTES = 15;
    const VIEWER_KEY = 'apex_cart_viewers';

    function getReserveUntil() {
      const stored = parseInt(sessionStorage.getItem(RESERVE_KEY), 10);
      if (stored && stored > Date.now()) return stored;
      const fresh = Date.now() + RESERVE_MINUTES * 60 * 1000;
      sessionStorage.setItem(RESERVE_KEY, String(fresh));
      return fresh;
    }

    function getViewerBase() {
      let n = parseInt(sessionStorage.getItem(VIEWER_KEY), 10);
      if (!n || n < 18 || n > 86) {
        n = 24 + Math.floor(Math.random() * 38); // 24..61
        sessionStorage.setItem(VIEWER_KEY, String(n));
      }
      return n;
    }

    function tickTimer() {
      const els = document.querySelectorAll('[data-apex-cart-timer]');
      if (!els.length) return;
      const until = getReserveUntil();
      let remain = Math.max(0, until - Date.now());
      if (remain === 0) {
        // Reset to fresh 15:00 when expired (keeps the urgency feel without breaking flow)
        sessionStorage.removeItem(RESERVE_KEY);
        remain = RESERVE_MINUTES * 60 * 1000;
        sessionStorage.setItem(RESERVE_KEY, String(Date.now() + remain));
      }
      const totalSec = Math.floor(remain / 1000);
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      const text = String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
      els.forEach(el => { el.textContent = text; });
    }

    function tickViewers() {
      const els = document.querySelectorAll('[data-apex-viewers]');
      if (!els.length) return;
      const base = getViewerBase();
      // tiny random drift +/- 2
      const drift = Math.floor(Math.random() * 5) - 2;
      const value = Math.max(18, Math.min(86, base + drift));
      els.forEach(el => { el.textContent = value; });
    }

    tickTimer(); tickViewers();
    setInterval(tickTimer, 1000);
    setInterval(tickViewers, 7000);
    // NOTE: previously had a MutationObserver here but it created an infinite
    // feedback loop (tick modifies DOM → observer fires → tick modifies DOM…).
    // The 1s setInterval already re-queries the DOM, so swapped-in cart HTML
    // gets picked up on the next tick automatically.
  }

  // ---------- 11. Init ----------
  function init() {
    setupReveals();
    setupCountUps();
    setupScrollBar();
    setupHeaderScroll();
    setupAnchors();
    attachAtcHandler();
    setupMobileStickyAtc();
    setupCartUrgency();
    // Magnetic only on devices with hover
    if (window.matchMedia('(hover: hover)').matches) setupMagnetic();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
